/**
 * A person's avatar: their picture when the platform gave an address (`picture` on a project member or
 * a directory person, D243), their initials otherwise or while it loads or 404s. The picture needs the
 * caller's token, so it is fetched with the bearer and shown from a blob address (api/client.ts).
 */

import { Avatar } from '@heroui/react';
import { useEffect, useState } from 'react';

import { fetchPicture } from '../../api/client.ts';

/** Up to two initials from a name or an address: "Jean-Pierre Simonis" is JS, "jp@mojoup.com.au" is J. */
export function personInitials(name: string): string {
  const words = name.split('@')[0]!.split(/[\s._-]+/).filter(Boolean);
  return ((words[0]?.[0] ?? '') + (words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '')).toUpperCase() || '?';
}

export function PersonAvatar({ name, picture, size = 'sm' }: { name: string; picture?: string; size?: 'sm' | 'md' | 'lg' }) {
  const [loaded, setLoaded] = useState<{ picture: string; src?: string }>();
  const src = loaded && loaded.picture === picture ? loaded.src : undefined;
  useEffect(() => {
    let live = true;
    if (picture) void fetchPicture(picture).then((u) => live && setLoaded({ picture, src: u }));
    return () => {
      live = false;
    };
  }, [picture]);
  return (
    <Avatar size={size} color="accent" variant="soft" className="shrink-0">
      {src ? <Avatar.Image src={src} alt="" /> : null}
      <Avatar.Fallback>{personInitials(name)}</Avatar.Fallback>
    </Avatar>
  );
}
